/**
 * Live Upload — client-side DICOM logic (Slice LU-A3/A4/A5/A6, logic lane).
 *
 * Pure, framework-free helpers the `/upload` page uses to validate a selected
 * study, scan + optionally strip PHI, and build the `multipart/related` body the
 * backend proxy streams to the device-server STOW receiver.
 *
 * Wire contract (must byte-match the receiver): the multipart framing mirrors
 * `a2z-stow-uploader/study_send/core.py`. See
 * NON_REPOS/plans/2026-06-09-live-upload-design.md §12.4.
 */
import dicomParser from "dicom-parser";

import type { PhiFinding, StudyValidation } from "../components/upload/types";
import { logger } from "./logger";

/** The slice of dicom-parser's DataSet we use (the module ships untyped). */
interface DicomDataSet {
  string(tag: string): string | undefined;
  elements: Record<
    string,
    {
      dataOffset: number;
      length: number;
      items?: { dataSet?: DicomDataSet }[];
    }
  >;
}

// DICOM tag keys (dicom-parser format) we read.
const TAG = {
  studyInstanceUid: "x0020000d",
  seriesInstanceUid: "x0020000e",
  sopInstanceUid: "x00080018",
  modality: "x00080060",
  studyDescription: "x00081030",
  studyDate: "x00080020",
} as const;

/**
 * Patient identity and demographic text fields removed by the browser scrubber.
 *
 * This is intentionally a custom policy, not a claim of DICOM PS3.15 Basic
 * Profile conformance. In particular, the product requirement is to retain all
 * UIDs, Accession Number, and study/series/acquisition/content timestamps. All
 * listed fields use string VRs, so replacing their value bytes with DICOM's
 * space padding remains length-preserving and does not require re-encoding the
 * Pixel Data. Nested DataSets are visited too, so repeated patient fields inside
 * sequences receive the same treatment.
 */
const PATIENT_INFO_TAGS: { tag: string; key: string }[] = [
  // Patient identity
  { tag: "PatientName", key: "x00100010" },
  { tag: "PatientID", key: "x00100020" },
  { tag: "IssuerOfPatientID", key: "x00100021" },
  { tag: "TypeOfPatientID", key: "x00100022" },
  { tag: "PatientBirthDate", key: "x00100030" },
  { tag: "PatientBirthTime", key: "x00100032" },
  { tag: "PatientBirthDateInAlternativeCalendar", key: "x00100033" },
  { tag: "PatientDeathDateInAlternativeCalendar", key: "x00100034" },
  { tag: "PatientAlternativeCalendar", key: "x00100035" },
  { tag: "PatientSex", key: "x00100040" },
  { tag: "OtherPatientIDs", key: "x00101000" },
  { tag: "OtherPatientNames", key: "x00101001" },
  { tag: "PatientBirthName", key: "x00101005" },
  { tag: "MedicalRecordLocator", key: "x00101090" },

  // Demographics and patient characteristics
  { tag: "PatientAge", key: "x00101010" },
  { tag: "PatientSize", key: "x00101020" },
  { tag: "PatientBodyMassIndex", key: "x00101022" },
  { tag: "MeasuredAPDimension", key: "x00101023" },
  { tag: "MeasuredLateralDimension", key: "x00101024" },
  { tag: "PatientWeight", key: "x00101030" },
  { tag: "PatientAddress", key: "x00101040" },
  { tag: "PatientMotherBirthName", key: "x00101060" },
  { tag: "MilitaryRank", key: "x00101080" },
  { tag: "BranchOfService", key: "x00101081" },
  { tag: "CountryOfResidence", key: "x00102150" },
  { tag: "RegionOfResidence", key: "x00102152" },
  { tag: "PatientTelephoneNumbers", key: "x00102154" },
  { tag: "PatientTelecomInformation", key: "x00102155" },
  { tag: "EthnicGroup", key: "x00102160" },
  { tag: "Occupation", key: "x00102180" },
  { tag: "SmokingStatus", key: "x001021a0" },
  { tag: "LastMenstrualDate", key: "x001021d0" },
  { tag: "PatientReligiousPreference", key: "x001021f0" },

  // Patient clinical/free-text fields that commonly contain identifying detail
  { tag: "MedicalAlerts", key: "x00102000" },
  { tag: "Allergies", key: "x00102110" },
  { tag: "AdditionalPatientHistory", key: "x001021b0" },
  { tag: "PatientComments", key: "x00104000" },

  // Veterinary/responsible-party identity fields
  { tag: "PatientSpeciesDescription", key: "x00102201" },
  { tag: "PatientSexNeutered", key: "x00102203" },
  { tag: "PatientBreedDescription", key: "x00102292" },
  { tag: "BreedRegistrationNumber", key: "x00102295" },
  { tag: "ResponsiblePerson", key: "x00102297" },
  { tag: "ResponsiblePersonRole", key: "x00102298" },
  { tag: "ResponsibleOrganization", key: "x00102299" },

  // Visit/admission identifiers and free text
  { tag: "AdmissionID", key: "x00380010" },
  { tag: "IssuerOfAdmissionID", key: "x00380011" },
  { tag: "ServiceEpisodeID", key: "x00380060" },
  { tag: "IssuerOfServiceEpisodeID", key: "x00380061" },
  { tag: "ServiceEpisodeDescription", key: "x00380062" },
  { tag: "CurrentPatientLocation", key: "x00380300" },
  { tag: "PatientInstitutionResidence", key: "x00380400" },
  { tag: "PatientState", key: "x00380500" },
  { tag: "VisitComments", key: "x00384000" },
];

export const PATIENT_INFO_FIELD_COUNT = PATIENT_INFO_TAGS.length;
export const DEFAULT_STRIP_PATIENT_INFO = true;

/** Thrown for a loud, user-facing validation failure (mirrors core.validate_study). */
export class StudyValidationError extends Error {}

/** One parsed instance, with its raw bytes retained for the upload body. */
interface ParsedInstance {
  bytes: Uint8Array;
  dataSet: DicomDataSet;
}

export interface ParsedStudy {
  studyInstanceUid: string;
  validation: StudyValidation;
  phiFindings: PhiFinding[];
  /** Per-instance bytes + dataset, in selection order, for building the body. */
  instances: ParsedInstance[];
}

/**
 * Lightweight representation of one study selected as part of a folder batch.
 *
 * Unlike `ParsedStudy`, this deliberately keeps no DICOM byte buffers or parsed
 * datasets. File handles are cheap to retain; bytes are reparsed immediately
 * before an upload (and again for a retry), which bounds a large folder's
 * in-memory DICOM payload to the active upload pool rather than every case.
 */
export interface FolderStudy {
  studyInstanceUid: string;
  validation: StudyValidation;
  phiFindings: PhiFinding[];
  files: File[];
}

function fmtStudyDate(raw: string | undefined): string | undefined {
  // DICOM DA is YYYYMMDD; surface as YYYY-MM-DD.
  if (!raw || raw.length !== 8) return undefined;
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/**
 * Build the ParsedStudy for one study's already-collected CT instances.
 *
 * Shared by parseStudy (single) and parseFolder (batch): study date is seeded
 * from the first instance; PHI is aggregated across EVERY instance (a later
 * series may carry identifying tags the first lacks), deduped by tag keeping the
 * first non-blank value seen for display (`stripPhi` blanks every instance
 * regardless).
 */
function assembleStudy(studyInstanceUid: string, instances: ParsedInstance[]): ParsedStudy {
  const seriesUids = new Set<string>();
  let totalBytes = 0;
  let studyDate: string | undefined;
  const phiByTag = new Map<string, string>();
  instances.forEach((inst, i) => {
    const seriesUid = inst.dataSet.string(TAG.seriesInstanceUid);
    if (seriesUid) seriesUids.add(seriesUid);
    if (i === 0) studyDate = fmtStudyDate(inst.dataSet.string(TAG.studyDate));
    for (const f of scanPhi(inst.dataSet)) {
      if (!phiByTag.has(f.tag)) phiByTag.set(f.tag, f.value);
    }
    totalBytes += inst.bytes.byteLength;
  });
  return {
    studyInstanceUid,
    validation: {
      studyInstanceUid,
      instanceCount: instances.length,
      seriesCount: seriesUids.size || undefined,
      totalBytes,
      acquiredDate: studyDate,
      modality: "CT",
    },
    phiFindings: Array.from(phiByTag, ([tag, value]) => ({ tag, value })),
    instances,
  };
}

/**
 * Parse + validate a selected study entirely in the browser.
 *
 * Skips files that aren't DICOM (parse throws) and non-CT instances — like the
 * CLI's discovery. Loudly rejects (throws `StudyValidationError`) when: no CT
 * instances remain, or the CT set spans more than one StudyInstanceUID. Returns
 * the retained bytes so the caller doesn't re-read the files.
 */
export async function parseStudy(files: File[]): Promise<ParsedStudy> {
  const instances: ParsedInstance[] = [];
  const studyUids = new Set<string>();
  let skippedNonCt = 0;

  for (const file of files) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let dataSet: DicomDataSet;
    try {
      dataSet = dicomParser.parseDicom(bytes) as DicomDataSet;
    } catch {
      // Not a parseable DICOM (e.g. DICOMDIR, stray file) — skip, like the CLI.
      continue;
    }

    const modality = dataSet.string(TAG.modality);
    if (modality && modality.toUpperCase() !== "CT") {
      skippedNonCt += 1;
      continue;
    }

    const studyUid = dataSet.string(TAG.studyInstanceUid);
    if (!studyUid) {
      throw new StudyValidationError("A CT instance is missing its StudyInstanceUID.");
    }
    studyUids.add(studyUid);
    instances.push({ bytes, dataSet });
  }

  if (instances.length === 0) {
    throw new StudyValidationError(
      skippedNonCt > 0
        ? "No CT instances found — this study has no CT series to upload."
        : "No DICOM files found in the selection.",
    );
  }
  if (studyUids.size > 1) {
    throw new StudyValidationError(
      `The selection spans ${studyUids.size} studies. Select a single study.`,
    );
  }
  if (skippedNonCt > 0) {
    logger.info(`Live Upload: skipped ${skippedNonCt} non-CT instance(s) in the selection`);
  }

  return assembleStudy(Array.from(studyUids)[0], instances);
}

/** Read the populated identifying tags from one instance's dataset. */
export function scanPhi(dataSet: DicomDataSet): PhiFinding[] {
  const found = new Map<string, string>();
  visitDataSets(dataSet, (current) => {
    for (const { tag, key } of PATIENT_INFO_TAGS) {
      const value = (current.string(key) || "").trim();
      if (value && !found.has(tag)) found.set(tag, value);
    }
  });
  return Array.from(found, ([tag, value]) => ({ tag, value }));
}

/** Visit the root DataSet and any parsed DataSets nested in sequence items. */
function visitDataSets(dataSet: DicomDataSet, visit: (current: DicomDataSet) => void): void {
  visit(dataSet);
  for (const element of Object.values(dataSet.elements)) {
    for (const item of element.items ?? []) {
      if (item.dataSet) visitDataSets(item.dataSet, visit);
    }
  }
}

/**
 * Return a copy of `bytes` with the identifying tags blanked in place.
 *
 * Length-preserving: each element's value bytes are overwritten with spaces
 * (0x20, valid padding for the string VRs these tags use), so offsets/lengths
 * stay intact and the file needs no re-encoding. Only tags physically present in
 * the dataset are touched.
 */
export function stripPhi(bytes: Uint8Array, dataSet: DicomDataSet): Uint8Array {
  const out = bytes.slice(); // copy — never mutate the caller's buffer
  visitDataSets(dataSet, (current) => {
    for (const { key } of PATIENT_INFO_TAGS) {
      const el = current.elements[key];
      if (!el || !el.length) continue;
      out.fill(0x20, el.dataOffset, el.dataOffset + el.length);
    }
  });
  return out;
}

/** A random multipart boundary token (non-crypto; just needs to not collide). */
export function makeBoundary(): string {
  return `a2zupload${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

export function multipartContentType(boundary: string): string {
  return `multipart/related; type="application/dicom"; boundary=${boundary}`;
}

/**
 * Assemble the `multipart/related` body as a Blob — one `application/dicom` part
 * per instance, framed exactly as the receiver parser expects (§12.4):
 *   \r\n--{boundary}\r\nContent-Type: application/dicom\r\n\r\n<bytes>
 * closed with \r\n--{boundary}--\r\n.
 *
 * When `strip` is true the supported patient-information fields are blanked
 * before framing. This is a patient-information scrub, not a claim that the
 * resulting DICOM is fully de-identified (e.g. burned-in pixel text is intact).
 */
export function buildMultipartBody(
  instances: ParsedInstance[],
  boundary: string,
  strip: boolean,
): Blob {
  const parts: BlobPart[] = [];
  for (const inst of instances) {
    parts.push(`\r\n--${boundary}\r\nContent-Type: application/dicom\r\n\r\n`);
    parts.push((strip ? stripPhi(inst.bytes, inst.dataSet) : inst.bytes) as BlobPart);
  }
  parts.push(`\r\n--${boundary}--\r\n`);
  return new Blob(parts, { type: "application/dicom" });
}

export interface FolderParseResult {
  /** One uploadable study per StudyInstanceUID (CT, within the size limit). */
  studies: FolderStudy[];
  /** Studies dropped from the batch, with a reason (e.g. over the size limit). */
  excludedStudies: { studyInstanceUid: string; reason: string }[];
  /** Files skipped before grouping, aggregated by reason. */
  skippedFiles: { reason: string; count: number }[];
}

/**
 * Parse a dropped/selected folder into one lightweight FolderStudy per
 * StudyInstanceUID.
 *
 * Unlike parseStudy (single-study, throws on a multi-study selection), this is
 * batch-friendly: it GROUPS by study, EXCLUDES oversize studies, and REPORTS
 * skipped files instead of throwing. Never throws on an empty/mixed folder — an
 * empty `studies` with a populated `skippedFiles` is a valid result.
 */
export async function parseFolder(files: File[], maxBytes: number): Promise<FolderParseResult> {
  // The accumulator intentionally contains only metadata and File references.
  // Do not add a parsed DataSet or Uint8Array here: a 100-study folder must not
  // retain its full DICOM payload until the user clicks Upload.
  const byStudy = new Map<
    string,
    {
      files: File[];
      instanceCount: number;
      totalBytes: number;
      seriesUids: Set<string>;
      studyDate?: string;
      phiByTag: Map<string, string>;
    }
  >();
  let notDicom = 0;
  let nonCt = 0;
  let missingUid = 0;

  for (const file of files) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let dataSet: DicomDataSet;
    try {
      dataSet = dicomParser.parseDicom(bytes) as DicomDataSet;
    } catch {
      notDicom += 1;
      continue;
    }
    const modality = dataSet.string(TAG.modality);
    if (modality && modality.toUpperCase() !== "CT") {
      nonCt += 1;
      continue;
    }
    const studyUid = dataSet.string(TAG.studyInstanceUid);
    if (!studyUid) {
      missingUid += 1;
      continue;
    }
    let study = byStudy.get(studyUid);
    if (!study) {
      study = {
        files: [],
        instanceCount: 0,
        totalBytes: 0,
        seriesUids: new Set<string>(),
        phiByTag: new Map<string, string>(),
      };
      byStudy.set(studyUid, study);
    }
    study.files.push(file);
    study.instanceCount += 1;
    study.totalBytes += bytes.byteLength;
    const seriesUid = dataSet.string(TAG.seriesInstanceUid);
    if (seriesUid) study.seriesUids.add(seriesUid);
    if (study.instanceCount === 1) study.studyDate = fmtStudyDate(dataSet.string(TAG.studyDate));
    for (const finding of scanPhi(dataSet)) {
      if (!study.phiByTag.has(finding.tag)) study.phiByTag.set(finding.tag, finding.value);
    }
  }

  const studies: FolderStudy[] = [];
  const excludedStudies: { studyInstanceUid: string; reason: string }[] = [];
  for (const [uid, study] of byStudy) {
    if (study.totalBytes > maxBytes) {
      excludedStudies.push({ studyInstanceUid: uid, reason: "over the size limit" });
    } else {
      studies.push({
        studyInstanceUid: uid,
        validation: {
          studyInstanceUid: uid,
          instanceCount: study.instanceCount,
          seriesCount: study.seriesUids.size || undefined,
          totalBytes: study.totalBytes,
          acquiredDate: study.studyDate,
          modality: "CT",
        },
        phiFindings: Array.from(study.phiByTag, ([tag, value]) => ({ tag, value })),
        files: study.files,
      });
    }
  }

  const skippedFiles: { reason: string; count: number }[] = [];
  if (notDicom) skippedFiles.push({ reason: "not a DICOM file", count: notDicom });
  if (nonCt) skippedFiles.push({ reason: "non-CT series", count: nonCt });
  if (missingUid) skippedFiles.push({ reason: "missing StudyInstanceUID", count: missingUid });
  if (skippedFiles.length || excludedStudies.length) {
    logger.info(
      `Live Upload (batch): ${studies.length} studies, ` +
        `${excludedStudies.length} excluded, skipped ${notDicom + nonCt + missingUid} files`,
    );
  }
  return { studies, excludedStudies, skippedFiles };
}
